import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Page from "@/components/Page";
import VoiceTextarea from "@/components/VoiceTextarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ACCOM } from "@/constants/testIds";
import { Accessibility } from "lucide-react";

export default function Accommodation() {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    accommodation_request: "",
    additional_info: "",
    preferred_contact_method: "email",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const submit = async () => {
    if (!form.accommodation_request.trim()) {
      toast.error("Please describe the accommodation you are requesting.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/accommodation", form);
      setSubmitted(true);
    } catch (e) {
      toast.error("Could not submit: " + (e?.response?.data?.detail || e.message));
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <Page>
        <div className="max-w-2xl mx-auto p-6 sm:p-10">
          <div className="p2p-card p-8">
            <Accessibility className="w-10 h-10" style={{ color: "#0B2D59" }} />
            <h1 className="font-serif text-3xl mt-3" style={{ color: "#0B2D59" }}>Thank you.</h1>
            <p className="mt-3" style={{ color: "#1F2937" }}>
              Your accommodation request has been received privately. It is kept separate from any rental application
              and will not be included in any decision criteria. We will contact you using your preferred method.
            </p>
            <div className="mt-6 flex gap-3">
              <button className="p2p-btn-primary" onClick={() => navigate("/")}>Back to home</button>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <div className="max-w-2xl mx-auto p-6 sm:p-10">
        <Link to="/" className="text-sm" style={{ color: "#475569" }}>← Back</Link>
        <h1 className="font-serif text-3xl sm:text-4xl mt-3" style={{ color: "#0B2D59" }}>
          Request an Accessibility Accommodation
        </h1>
        <p className="mt-3" style={{ color: "#1F2937" }}>
          You do <b>not</b> need to share a diagnosis. This request is private and is sent to a separate inbox.
          It is not used in deciding your application.
        </p>
        <div className="p2p-card p-6 sm:p-8 mt-6 space-y-5">
          <Field label="Your name (optional)">
            <input className="p2p-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Best way to reach you">
            <input className="p2p-input" data-testid={ACCOM.contact} placeholder="Phone or email"
              value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
          </Field>
          <Field label="What change or accommodation are you requesting?">
            <VoiceTextarea
              testId={ACCOM.request} micTestId="accom-request-mic"
              rows={4} maxChars={1500}
              value={form.accommodation_request}
              onChange={(v) => setForm({ ...form, accommodation_request: v })}
              placeholder="e.g., I would benefit from written communication instead of phone calls." />
          </Field>
          <Field label="Anything else you'd like us to consider? (optional)">
            <VoiceTextarea
              testId={ACCOM.additional} micTestId="accom-additional-mic"
              rows={4} maxChars={1500}
              value={form.additional_info}
              onChange={(v) => setForm({ ...form, additional_info: v })} />
          </Field>
          <Field label="Preferred method of communication">
            <select className="p2p-input" value={form.preferred_contact_method}
              onChange={(e) => setForm({ ...form, preferred_contact_method: e.target.value })}>
              <option value="email">Email</option>
              <option value="phone">Phone call</option>
              <option value="text">Text message</option>
            </select>
          </Field>
          <button className="p2p-btn-primary w-full" data-testid={ACCOM.submit} disabled={submitting} onClick={submit}>
            {submitting ? "Sending…" : "Send request"}
          </button>
        </div>
      </div>
    </Page>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="p2p-label">{label}</span>
      {children}
    </label>
  );
}
